import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'latest-address');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' as const });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.categoryId === activeCategory);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-3">
              {currentCategory?.name || '分类页面'}
            </h1>
            <p className="text-foreground-500 text-sm">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-[1200px] mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                          {article.readTime}
                        </span>
                        <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-500 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${currentCategory?.name || '分类'} - www91官方导航`,
              description: currentCategory?.description || '',
              url: `https://guifeizaixian.cn/category/${activeCategory}`,
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://guifeizaixian.cn/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: currentCategory?.name || '分类',
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}